import { useEffect, useState, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import API from '../../api';

export default function PasserQCM() {
  const { evaluationId } = useParams();
  const navigate = useNavigate();
  const [questions, setQuestions] = useState([]);
  const [, setTentative] = useState(null);
  const [reponses, setReponses] = useState({});
  const [timeLeft, setTimeLeft] = useState(0);
  const [submitted, setSubmitted] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const timerRef = useRef(null);

  useEffect(() => {
    API.post(`evaluations/${evaluationId}/demarrer-qcm/`)
      .then((res) => {
        setTentative(res.data.tentative);
        setQuestions(res.data.questions);
        setTimeLeft(res.data.temps_restant_secondes);
        if (res.data.tentative.terminee) {
          setSubmitted(true);
          setError('Ce QCM a déjà été soumis.');
        }
      })
      .catch((err) => {
        setError(err.response?.data?.detail || 'Impossible de démarrer le QCM.');
      });
    return () => clearInterval(timerRef.current);
  }, [evaluationId]);

  useEffect(() => {
    if (timeLeft <= 0 || submitted) return;
    timerRef.current = setInterval(() => {
      setTimeLeft((t) => {
        if (t <= 1) {
          clearInterval(timerRef.current);
          handleSubmit();
          return 0;
        }
        return t - 1;
      });
    }, 1000);
    return () => clearInterval(timerRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timeLeft, submitted]);

  const handleAnswer = (questionId, answer) => {
    setReponses((prev) => ({ ...prev, [questionId]: answer }));
  };

  const handleSubmit = useCallback(async () => {
    if (submitted) return;
    setSubmitted(true);
    clearInterval(timerRef.current);
    const payload = {
      reponses: Object.entries(reponses).map(([qid, rep]) => ({
        question_id: qid, reponse: rep,
      })),
    };
    try {
      const res = await API.post(`evaluations/${evaluationId}/soumettre-qcm/`, payload);
      setResult(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Erreur lors de la soumission.');
    }
  }, [evaluationId, reponses, submitted]);

  const formatTime = (s) => {
    const m = Math.floor(s / 60);
    const sec = s % 60;
    return `${m}:${sec.toString().padStart(2, '0')}`;
  };

  if (error && !questions.length) {
    return (
      <div>
        <div className="alert alert-error"><i className="bi bi-exclamation-triangle" /> {error}</div>
        <button className="btn btn-secondary" onClick={() => navigate('/etudiant/evaluations')}>
          <i className="bi bi-arrow-left" /> Retour
        </button>
      </div>
    );
  }

  if (result) {
    return (
      <div>
        <div className="page-header">
          <h2><i className="bi bi-trophy" /> Résultat du QCM</h2>
        </div>
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-icon green"><i className="bi bi-award" /></div>
            <div className="stat-info"><h3>{result.score}/{result.bareme || '?'}</h3><p>Score</p></div>
          </div>
          <div className="stat-card">
            <div className="stat-icon blue"><i className="bi bi-check-circle" /></div>
            <div className="stat-info"><h3>{result.bonnes_reponses}/{result.total_questions}</h3><p>Bonnes réponses</p></div>
          </div>
        </div>
        <div className="form-actions">
          <button className="btn btn-primary" onClick={() => navigate('/etudiant/evaluations')}>
            <i className="bi bi-arrow-left" /> Retour aux évaluations
          </button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="qcm-header">
        <h2><i className="bi bi-patch-question" /> QCM en cours</h2>
        <div className={`qcm-timer ${timeLeft < 60 ? 'timer-danger' : ''}`}>
          <i className="bi bi-clock" /> {formatTime(timeLeft)}
        </div>
      </div>

      <div className="qcm-progress">
        <i className="bi bi-list-check" /> {Object.keys(reponses).length} / {questions.length} répondues
      </div>

      <div className="qcm-questions">
        {questions.map((q, idx) => (
          <div key={q.id} className="qcm-question">
            <p className="qcm-enonce"><strong>Q{idx + 1}.</strong> {q.enonce}</p>
            <div className="qcm-choices">
              {['A', 'B', 'C', 'D'].map((letter) => {
                const text = q[`choix_${letter.toLowerCase()}`];
                if (!text) return null;
                return (
                  <label
                    key={letter}
                    className={`qcm-choice ${reponses[q.id] === letter ? 'selected' : ''}`}
                  >
                    <input
                      type="radio"
                      name={`q-${q.id}`}
                      value={letter}
                      checked={reponses[q.id] === letter}
                      onChange={() => handleAnswer(q.id, letter)}
                      disabled={submitted}
                    />
                    <span className="choice-letter">{letter}</span> {text}
                  </label>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      {!submitted && (
        <div className="form-actions">
          <button className="btn btn-primary" onClick={handleSubmit}>
            <i className="bi bi-send" /> Soumettre le QCM
          </button>
        </div>
      )}
    </div>
  );
}
